import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, useController } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Grid, Box, Typography, Divider } from '@mui/material';
import { CloseIcon } from '../../../assets';
import {
    CustomReactSelect,
    CustomButton,
    CustomSwitch,
    Loader,
} from '../../shared';
import {
    getCampaignTriggers,
    getSelectedCampaignPartners,
    getSelectedCampaignChannels,
    createCampaign,
    updateCampaign,
    getCampaignDetails,
} from '../../../redux/thunks/campaignThunks';
import {
    selectCampaignPartnerOptions,
    selectCampaignTriggerOptions,
    selectCampaignChannelOptions,
} from '../../../redux/reducers/campaignReducer';
import SFTPConnection from './sftpConnection';
import CampaignMessageForm from './campaignMessageForm';
import { daysOptions, hourlyDropdownOptions, validateForScriptTag } from '../../../utility';
import { daysOrder } from '../../../constants';

const initialValue = {
    trigger: [],
    campaignName: '',
    channel: '',
    partner: '',
    day: 'Monday',
    time: 1,
    autoScheduler: false,
    templateId: null,
};

const campaignManual = yup
    .object({
        trigger: yup
            .array()
            .min(1, 'Please Select at least one Trigger')
            .required('Please Select Trigger'),
        campaignName: yup.string().required('Campaign Name is required').test('no-script-tag', 'Script tags are not allowed.', validateForScriptTag),
        channel: yup.string().required('Please Select Channel'),
        partner: yup.string().required('Please Select Partner'),
        day: yup.string().required('Please Select Day'),
        time: yup.number().required('Please Select Time'),
        autoScheduler: yup.boolean().default(false),
    })
    .required();

function CreateManualCampaign(props) {
    const { campaignId, handleClose } = props;
    const dispatch = useDispatch();

    const { company } = useSelector((state) => state.company);
    const { isCampaignCreated, isCampaignDataLoading, campaign } = useSelector(
        (state) => state.campaign
    );
    const campaignTriggers = useSelector(selectCampaignTriggerOptions());
    const campaignPartners = useSelector(selectCampaignPartnerOptions());
    const campaignChannels = useSelector(selectCampaignChannelOptions());
    const {
        register,
        handleSubmit,
        setValue,
        getValues,
        formState: { errors },
        control,
        watch,
    } = useForm({
        resolver: yupResolver(campaignManual),
        defaultValues: initialValue,
    });
    const updatedCampaignName = watch('campaignName');

    const {
        field: {
            value: trigger,
            onChange: onTriggerChange,
            ...restTriggerProps
        },
    } = useController({ name: 'trigger', control });
    const {
        field: {
            value: channel,
            onChange: onChannelChange,
            ...restChannelProps
        },
    } = useController({ name: 'channel', control });
    const {
        field: {
            value: partner,
            onChange: onPartnerChange,
            ...restPartnerProps
        },
    } = useController({ name: 'partner', control });
    const {
        field: { value: day, onChange: onDayChange, ...restDayProps },
    } = useController({ name: 'day', control });
    const {
        field: { value: time, onChange: onTimeChange, ...restTimeProps },
    } = useController({ name: 'time', control });
    const {
        field: {
            value: autoScheduler,
            onChange: onAutoSchedulerChange,
            ...restAutoSchedulerProps
        },
    } = useController({ name: 'autoScheduler', control });

    const getCampaignGenerationTypeData = () => {
        if (company.id) {
            dispatch(getCampaignTriggers({ companyId: company.id }));
            dispatch(getSelectedCampaignPartners({ companyId: company.id }));
            dispatch(getSelectedCampaignChannels({ companyId: company.id }));
        }
    };

    const handleTriggerChange = (selectedOption, actionMeta) => {
        const currentTriggerValue = getValues('trigger');
        if (
            actionMeta.action === 'remove-value' ||
            actionMeta.action === 'pop-value'
        ) {
            const removedOption = actionMeta.removedValue;
            const indexToRemove = currentTriggerValue.findIndex(
                (item) => item.id === removedOption.id
            );

            if (indexToRemove !== -1) {
                currentTriggerValue.splice(indexToRemove, 1);
                setValue('trigger', [...currentTriggerValue]);
            }
        } else {
            setValue('trigger', [...selectedOption]);
        }
    };

    const handleChannelChange = (selectedOption) => {
        onChannelChange(selectedOption ? selectedOption.value : selectedOption);
        setValue('templateId', selectedOption.templateId);
    };

    const handleCampaignNameChange = (e) => setValue('campaignName', e.target.value);

    const onSubmit = (data) => {
        let createCampaignData = {
            auto_scheduler: data.autoScheduler === true ? 1 : 0,
            estimated_duration: '',
            hour: 0,
            name: data.campaignName,
            partner_id: data.partner,
            schedule: 'Monday',
            targeted_duration: '',
            triggers: data.trigger?.map((item) => item.id),
            type: data.channel,
        };
        dispatch(
            createCampaign({ companyId: company.id, data: createCampaignData })
        );
    };

    const dispatchUpdateCampaignAction = () => {
        let updateCampaignData = {
            auto_scheduler: getValues('autoScheduler') === true ? 1 : 0,
            estimated_duration: '',
            hour: getValues('time') - 0,
            name: updatedCampaignName,
            partner_id: getValues('partner'),
            schedule: getValues('day'),
            targeted_duration: '',
            triggers: getValues('trigger')?.map((item) => item.id),
            type: getValues('channel'),
        };

        dispatch(
            updateCampaign({ companyId: company.id, campaignId: campaign.id, data: updateCampaignData })
        );        
    };

    const handleSave = () => {
        handleSubmit(onSubmit);
    };

    const getCampaignsData = () => {
        return {
            auto_scheduler: getValues('autoScheduler') === true ? 1 : 0,
            estimated_duration: '',
            hour: getValues('time') - 0,
            name: updatedCampaignName,
            partner_id: getValues('partner'),
            schedule: getValues('day'),
            targeted_duration: '',
            triggers: getValues('trigger')?.map((item) => item.id),
            type: getValues('channel'),
        }
    };

    useEffect(() => {
        getCampaignGenerationTypeData();
    }, [company]);

    useEffect(() => {
        if (campaignId) {
            dispatch(
                getCampaignDetails({
                    companyId: company.id,
                    campaignId: campaignId,
                })
            );
        }
    }, [campaignId]);

    useEffect(() => {
        if (campaign.id) {
            setValue(
                'trigger',
                campaign.triggers?.map((trigger) => ({
                    name: trigger.name,
                    id: trigger.id,
                    label: trigger.name,
                    value: trigger.id,
                }))
            );
            setValue('campaignName', campaign.name);
            setValue('channel', campaign.type);
            setValue('partner', campaign.partner_id);
            setValue('day', campaign.schedule);
            setValue('time', campaign.hour);
            setValue('templateId', campaign?.campaign_type?.template_id);
            setValue('autoScheduler', campaign.auto_scheduler ? true : false);
        }
    }, [campaign]);
    
    return (
        <Grid container className="custom-modal-inner-container">
            <form
                id="manualCampaign"
                onSubmit={handleSubmit(onSubmit)}
                className="w-100"
            >
                <Grid item xs={12}>
                    <Box className="padding-24px">
                        {/* Campaign */}
                        <Box className="flex-row-space-between pb-24px">
                            <Typography className="text-lg-medium gray-900">
                                {campaignId
                                    ? 'Edit Campaign'
                                    : 'Create new campaign'}
                            </Typography>
                            <Box className="icon" onClick={handleClose}>
                                <CloseIcon
                                    color="#98A2B3"
                                    width="12"
                                    height="12"
                                />
                            </Box>
                        </Box>
                        {isCampaignDataLoading ? (
                            <Loader />
                        ) : (
                            <>
                                {/* Select Trigger */}
                                <Box className="pb-24px">
                                    <CustomReactSelect
                                        label="Select Queries"
                                        selectedOption={trigger}
                                        options={campaignTriggers}
                                        multipleSelect={true}
                                        handleFilter={(option, actionMeta) =>
                                            handleTriggerChange(
                                                option,
                                                actionMeta
                                            )
                                        }
                                        placeholder="Select Triggers"
                                        {...restTriggerProps}
                                    />
                                    <span className="text-xs-regular error-500">
                                        {errors.trigger?.message}
                                    </span>
                                </Box>

                                {/* Campaign Name */}
                                <Box className="flex-column gap-6 w-100 pb-24px">
                                    <Typography className="text-sm-medium gray-500">
                                        Campaign Name
                                    </Typography>

                                    <input
                                        type="text"
                                        placeholder="Campaign Name"
                                        className="custom-text-field padding-tb-10-rl-14"
                                        value={updatedCampaignName} 
                                        onChange={handleCampaignNameChange}
                                    />
                                    <span className="text-xs-regular error-500">
                                        {errors.campaignName?.message}
                                    </span>
                                </Box>
                                {/* Channel and Partner */}
                                <Box className="flex-row gap-12">
                                    {/* Select Channel */}
                                    <Box className="w-50 pb-24px">
                                        <CustomReactSelect
                                            label="Select Channel"
                                            selectedOption={
                                                channel
                                                    ? campaignChannels.find(
                                                          (x) =>
                                                              x.value ===
                                                              channel
                                                      )
                                                    : channel
                                            }
                                            options={campaignChannels}
                                            handleFilter={(option) =>
                                                handleChannelChange(option)
                                            }
                                            placeholder="Select Channel"
                                            disable={isCampaignCreated}
                                            {...restChannelProps}
                                        />
                                        <span className="text-xs-regular error-500">
                                            {errors.channel?.message}
                                        </span>
                                    </Box>

                                    {/* Select Partner */}
                                    <Box className="w-50 pb-24px">
                                        <CustomReactSelect
                                            label="Select Partner"
                                            selectedOption={
                                                partner
                                                    ? campaignPartners.find(
                                                          (x) =>
                                                              x.value ===
                                                              partner
                                                      )
                                                    : partner
                                            }
                                            options={campaignPartners}
                                            handleFilter={(option) =>
                                                onPartnerChange(
                                                    option
                                                        ? option.value
                                                        : option
                                                )
                                            }
                                            placeholder="Select Partner"
                                            disable={isCampaignCreated}
                                            {...restPartnerProps}
                                        />
                                        <span className="text-xs-regular error-500">
                                            {errors.partner?.message}
                                        </span>
                                    </Box>
                                </Box>
                                {isCampaignCreated ? (
                                    <>
                                        {
                                            !getValues('autoScheduler') ? 
                                            (
                                                <>
                                                    {/* Duration Time and Day */}
                                                    <Box className="pb-24px">
                                                        <Grid
                                                            container
                                                            spacing={'24px'}
                                                            className="flex-row-end"
                                                        >
                                                            <Grid item xs={6}>
                                                                <CustomReactSelect
                                                                    label="Export to Partner Every"
                                                                    options={daysOptions}
                                                                    placeholder="Select Day"
                                                                    selectedOption={
                                                                        day
                                                                            ? daysOptions.find(
                                                                                (x) =>
                                                                                    x.value ===
                                                                                    day
                                                                            )
                                                                            : day
                                                                    }
                                                                    handleFilter={(
                                                                        option
                                                                    ) =>
                                                                        onDayChange(
                                                                            option
                                                                                ? option.value
                                                                                : option
                                                                        )
                                                                    }
                                                                    {...restDayProps}
                                                                    isSorted={true}
                                                                />
                                                                <span className="text-xs-regular error-500">
                                                                    {errors.day?.message}
                                                                </span>
                                                            </Grid>
                                                            <Grid item xs={6}>
                                                                <CustomReactSelect
                                                                    options={
                                                                        hourlyDropdownOptions
                                                                    }
                                                                    placeholder="Select Time"
                                                                    selectedOption={
                                                                        time
                                                                            ? hourlyDropdownOptions.find(
                                                                                (x) =>
                                                                                    x.value ===
                                                                                    time
                                                                            )
                                                                            : time
                                                                    }
                                                                    handleFilter={(
                                                                        option
                                                                    ) =>
                                                                        onTimeChange(
                                                                            option
                                                                                ? option.value
                                                                                : option
                                                                        )
                                                                    }
                                                                    {...restTimeProps}
                                                                />
                                                                <span className="text-xs-regular error-500">
                                                                    {errors.time?.message}
                                                                </span>
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                </>
                                            ) : null
                                        }

                                        <Box className="min-width-50">
                                            <CustomSwitch
                                                label="Auto Scheduler"
                                                checked={autoScheduler}
                                                onChange={onAutoSchedulerChange}
                                                {...restAutoSchedulerProps}
                                            />
                                        </Box>
                                    </>
                                ) : (
                                    <Box className="pb-24px">
                                        <CustomButton
                                            title="Create Campaign"
                                            titleColor="#FFFFFF"
                                            buttonColor="#AD509E"
                                            type="submit"
                                            handleClick={handleSave}
                                        />
                                    </Box>
                                )}
                            </>
                        )}
                    </Box>
                </Grid>
            </form>
            {isCampaignCreated ? (
                <>
                    <SFTPConnection partnerId={campaign.partner_id} handleCampaignData={getCampaignsData} />
                    <CampaignMessageForm
                        templateId={getValues('templateId') ?? 1}
                        campaignId={campaign.id}
                        handleClose={handleClose}
                        handleUpdateCampaign={dispatchUpdateCampaignAction}
                    />
                </>
            ) : null}
        </Grid>
    );
}

CreateManualCampaign.defaultProps = {
    campaignId: null,
};

export default CreateManualCampaign;
